import { Blog, Feedback, QuickSearch, NewsletterConfirmation } from "./community.tsx";
import { NoteRuntime, NotesPage } from "./notes.tsx";
import { FocusRuntime, FocusPage } from "./focus.tsx";
import { PlannerPage, revokeLocalPush } from "./planner.tsx";
import { HabitsPage } from "./habits.tsx";
import { RewardsPage, BreakGame } from "./rewards.tsx";
import { RevisionPage } from "./revision.tsx";
import { DictionaryLayer } from "./revision-ui.tsx";
import { StudyIndicator, useToday } from "./productivity.tsx";
import { ReportPage } from "./report.tsx";
import { DayEnd } from "./day-end.tsx";
import { TodayPage } from "./today.tsx";
import { SHOW_NEW_BADGES, NewBadge } from "./flags.tsx";
import { latestScore, useAchievements, useScores, useSideCounts } from "./side-counts.ts";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  type AnchorHTMLAttributes,
} from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import {
  type Account,
  legacyDrillRoute,
  ProfileInput,
  SettingsInput,
  type Settings,
} from "@ca/shared";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Fish,
  Logo,
  Modal,
  PageHead,
  Seg,
  Skeleton,
  SpaceTag,
  TipLayer,
  Toasts,
  type ToastItem,
} from "@ca/ui";
import { HttpError, loadMe, loginUrl, logout, setIdentity } from "./api.ts";
import { WriteQueue, type PendingWrite, type SyncStatus } from "./queue.ts";
import { navigation, pageMeta, spaces, type Space } from "./navigation.ts";
import { hiddenPath, featureOn } from "./features.ts";
import "@ca/ui/fonts.css";
import "@ca/ui/tokens.css";
import "@ca/ui/proto.css";
import "./app.css";
import { cacheOfflineShell } from "./offline.ts";
import { LearningPage } from "./learning.tsx";
import { ProPage, UpsellDialog, usePro } from "./pro.tsx";

if (import.meta.env.VITE_SENTRY_DSN)
  Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, sendDefaultPii: false });
const client = new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 15000 } } });
const queue = new WriteQueue("ca-sync", undefined, () =>
  window.dispatchEvent(new Event("ca-sync")),
);
interface Context {
  me: Account;
  save: (path: string, method: string, body: unknown, optimistic: Account) => Promise<void>;
  notify: (message: string) => void;
}
function Link({ to, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const navigate = useNavigate();
  return (
    <a
      {...props}
      href={to}
      onClick={(e) => {
        props.onClick?.(e);
        if (
          !e.defaultPrevented &&
          e.button === 0 &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey
        ) {
          e.preventDefault();
          void navigate(to === "/" ? { to: "/" } : { to: "/$", params: { _splat: to.slice(1) } });
        }
      }}
    />
  );
}

const AccountContext = createContext<Context | null>(null);
function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("Account context missing");
  return ctx;
}
function Root() {
  return <Outlet />;
}
function Login() {
  const [error, setError] = useState("");
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("error"))
      setError("Không hoàn tất được đăng nhập. Thử lại.");
  }, []);
  return (
    <main className="gate">
      <div className="modal stack" style={{ gap: 14 }}>
        <div style={{ width: 130, margin: "0 auto" }}>
          <Fish />
        </div>
        <h1 style={{ textAlign: "center", fontSize: 26 }}>Chào mừng đến Cá Toeic</h1>
        <p className="muted" style={{ textAlign: "center" }}>
          Học đúng chỗ bạn yếu — đăng nhập để bắt đầu.
        </p>
        {error && <p role="alert">{error}</p>}
        <a className="btn btn-dark btn-lg" href={loginUrl}>
          Tiếp tục với Google →
        </a>
        <p className="faint" style={{ textAlign: "center", fontSize: 12.5, fontWeight: 600 }}>
          Dành cho Chrome trên máy tính.
        </p>
      </div>
    </main>
  );
}
function Protected({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const notify = useCallback((message: string) => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list.slice(-2), { id, message }]);
    setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, 2600);
  }, []);
  const query = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const server = await loadMe();
        const pending = await queue.list(server.id);
        const local = pending.length ? await queue.cached(server.id) : undefined;
        sessionStorage.setItem("ca-account", server.id);
        if (!pending.length) await queue.cache(server);
        return local ?? server;
      } catch (error) {
        if (error instanceof HttpError) {
          if (error.status === 401) sessionStorage.removeItem("ca-account");
          throw error;
        }
        const id = sessionStorage.getItem("ca-account");
        const local = id ? await queue.cached(id) : undefined;
        if (local) {
          setIdentity(local);
          return local;
        }
        throw error;
      }
    },
  });
  const me = query.data;
  useEffect(() => {
    queue.activate(me?.id ?? null);
    return () => {
      queue.activate(null);
    };
  }, [me?.id]);
  useEffect(() => {
    if (query.error instanceof HttpError && query.error.status === 401)
      void navigate({ to: "/login" });
    else if (me?.activationRequired && location.pathname !== "/pro")
      void navigate({ to: "/$", params: { _splat: "pro" } });
    else if (!me?.activationRequired && me?.needsOnboarding && location.pathname !== "/welcome")
      void navigate({ to: "/$", params: { _splat: "welcome" } });
    else if (
      me &&
      !me.activationRequired &&
      !me.needsOnboarding &&
      location.pathname === "/welcome"
    )
      void navigate({ to: "/" });
  }, [query.error, me, location.pathname, navigate]);
  useEffect(() => {
    if (!me) return;
    document.documentElement.dataset.skin = me.profile.settings.skin;
    document.documentElement.dataset.theme = me.profile.settings.skin === "dark" ? "dark" : "light";
  }, [me]);
  useEffect(() => {
    const sync = (e?: Event) => {
      void queue
        .drain((account) => {
          queryClient.setQueryData<Account>(["me"], (current) =>
            current && current.id === account.id && current.profile.rev > account.profile.rev
              ? current
              : account,
          );
        }, e?.type === "online")
        .catch((error: unknown) => {
          notify(error instanceof Error ? error.message : "Chưa gửi được.");
        });
    };
    window.addEventListener("online", sync);
    const timer = setInterval(sync, 10000);
    if (me) sync();
    return () => {
      window.removeEventListener("online", sync);
      clearInterval(timer);
    };
  }, [me?.id, queryClient, notify]);
  if (query.isPending)
    return (
      <main className="gate">
        <Skeleton />
      </main>
    );
  if (query.isError)
    return (
      <main className="gate">
        <ErrorState
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </main>
    );
  if (!me) return null;
  const save = async (path: string, method: string, body: unknown, optimistic: Account) => {
    await queue.enqueue({ accountId: me.id, resource: "account", path, method, body }, optimistic);
    queryClient.setQueryData(["me"], optimistic);
    notify("Đã lưu trên máy.");
    void queue.drain((account) => {
      queryClient.setQueryData<Account>(["me"], (current) =>
        current && current.id === account.id && current.profile.rev > account.profile.rev
          ? current
          : account,
      );
    });
  };
  return (
    <AccountContext value={{ me, save, notify }}>
      {children}
      <Toasts items={toasts} />
      <TipLayer />
    </AccountContext>
  );
}
function Layout() {
  return (
    <Protected>
      <Shell />
    </Protected>
  );
}
/**
 * Trạng thái đồng bộ (QUYET-DINH S6): ẩn khi mọi thay đổi đã lên server, chỉ hiện chip vàng
 * "Đang đồng bộ…" / "Chưa đồng bộ" cạnh viên giờ học khi có việc đang chờ hoặc lỗi.
 */
function SyncIndicator() {
  const [state, setState] = useState<SyncStatus>("synced");
  const [pending, setPending] = useState<PendingWrite[]>([]);
  const [open, setOpen] = useState(false);
  const { notify } = useAccount();
  const queryClient = useQueryClient();
  useEffect(() => {
    const update = () => {
      void Promise.all([queue.status(), queue.list()]).then(([s, p]) => {
        setState(s);
        setPending(p);
      });
    };
    window.addEventListener("ca-sync", update);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("ca-sync", update);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return (
    <>
      {state !== "synced" && (
        <button
          className="chip warn sync-chip"
          data-tip={
            state === "saved"
              ? "Đang đồng bộ…|Đã lưu trên máy, đang chờ gửi lên máy chủ."
              : "Chưa đồng bộ|Chưa gửi được. Đã lưu trên máy, bấm để xem chi tiết."
          }
          aria-label={
            state === "saved"
              ? "Đang đồng bộ… · Đã lưu trên máy"
              : "Chưa đồng bộ · Chưa gửi được · Đã lưu trên máy"
          }
          onClick={() => {
            setOpen(true);
          }}
        >
          {state === "saved" ? "◌ Đang đồng bộ…" : "! Chưa đồng bộ"}
        </button>
      )}
      {open && (
        <Modal
          title="Trạng thái đồng bộ"
          onClose={() => {
            setOpen(false);
          }}
        >
          <div className="stack">
            <p className="muted">Chuyển máy chỉ khôi phục được phần đã đồng bộ.</p>
            {state === "synced" && <p>✓ Mọi thay đổi đã được đồng bộ.</p>}
            {pending.map((p) => (
              <div key={p.key} className="card tight" style={{ background: "var(--surface-2)" }}>
                <p>{p.error ?? "Đã lưu trên máy, đang chờ gửi."}</p>
                {p.state === "blocked" && (
                  <>
                    <p className="muted">Bản sửa trên máy được giữ lại để bạn đối chiếu:</p>
                    <pre className="local-copy">{JSON.stringify(p.body, null, 2)}</pre>
                  </>
                )}
              </div>
            ))}
            <div className="row">
              <Button
                onClick={() => {
                  void queue.drain((me) => {
                    queryClient.setQueryData(["me"], me);
                  });
                }}
              >
                Gửi lại
              </Button>
              {pending.some((p) => p.state === "blocked") && (
                <Button
                  variant="line"
                  onClick={() => {
                    void (async () => {
                      // Tải được bản server trước khi xoá bản sửa trên máy.
                      try {
                        const server = await loadMe();
                        await queue.discardResource("account");
                        await queue.cache(server);
                        queryClient.setQueryData(["me"], server);
                        setOpen(false);
                        notify("Đã dùng bản trên máy chủ.");
                      } catch (e) {
                        notify(e instanceof Error ? e.message : "Chưa kết nối được.");
                      }
                    })();
                  }}
                >
                  Bỏ bản trên máy, dùng bản server
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
/** Thứ tự chấm giao diện như bản mẫu: kem · hồng · tối · xanh. */
const SKIN_DOTS: [Settings["skin"], string][] = [
  ["cream", "#EFECE5"],
  ["pink", "#F2D3E0"],
  ["dark", "#17161B"],
  ["blue", "#C4D4EA"],
];
function Side({
  path,
  space,
  drawer,
  onDrawer,
  update,
}: {
  path: string;
  space: Space;
  drawer: boolean;
  onDrawer: (open: boolean) => void;
  update: (patch: Partial<Settings>) => void;
}) {
  const { me } = useAccount();
  const navigate = useNavigate();
  const counts = useSideCounts(me.id);
  const today = useToday(me.id);
  const scores = useScores(me.id);
  const pro = usePro(me.id);
  const settings = me.profile.settings;
  const name = me.profile.displayName ?? me.name ?? "Bạn";
  const score = latestScore(scores.data);
  const proActive =
    !!pro.data?.active &&
    (pro.data.expiresAt === null || new Date(pro.data.expiresAt) > new Date());
  const go = (to: string) => {
    onDrawer(false);
    void navigate(to === "/" ? { to: "/" } : { to: "/$", params: { _splat: to.slice(1) } });
  };
  const current = (to: string) => path === to || path.startsWith(`${to}/`);
  return (
    <>
      <aside className={`side${drawer ? " open" : ""}`} id="side" aria-label="Menu">
        <div className="brand">
          <Logo />
          <div>
            <b>Cá Toeic</b>
            <small>Học đúng chỗ bạn yếu</small>
          </div>
        </div>
        <Link
          to="/"
          className="today-link"
          aria-label="Hôm nay"
          aria-current={path === "/" ? "page" : undefined}
          onClick={() => {
            onDrawer(false);
          }}
        >
          <span>🏠</span>
          <span className="lbl">Hôm nay</span>
          <span className="tl-min">
            {Math.floor((today.data?.activeSecs ?? 0) / 60)}/
            {today.data?.goalMinutes ?? me.profile.dailyGoalMinutes}p
          </span>
        </Link>
        <div className="spaceswitch four" role="group" aria-label="Chọn khu">
          {spaces.map((s) => (
            <button
              key={s.key}
              aria-pressed={space === s.key}
              data-tip={s.tip}
              onClick={() => {
                go(s.home);
              }}
            >
              <span className="ss-ic">{s.icon}</span>
              <b>{s.label}</b>
            </button>
          ))}
        </div>
        <nav className="nav" aria-label={`Khu ${spaces.find((s) => s.key === space)?.label ?? ""}`}>
          {navigation[space].map((n) =>
            "group" in n ? (
              <div key={n.group} className="grp">
                {n.group}
              </div>
            ) : "action" in n ? (
              <a
                key="note"
                role="button"
                tabIndex={0}
                data-tip="Ghi nhanh|Ghi ngay trên trang đang mở, không phải rời bài. Lưu về khu Take note › Tất cả ghi chú"
                onClick={() => {
                  onDrawer(false);
                  window.dispatchEvent(new Event("ca-note-open"));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.dispatchEvent(new Event("ca-note-open"));
                  }
                }}
              >
                <span className="ic">{n.icon}</span>
                <span className="lbl">{n.label}</span>
              </a>
            ) : (
              <Link
                key={n.path}
                to={n.path}
                aria-label={n.label}
                aria-current={current(n.path) ? "page" : undefined}
                onClick={() => {
                  onDrawer(false);
                }}
              >
                <span className="ic">{n.icon}</span>
                <span className="lbl">{n.label}</span>
                {n.isNew && SHOW_NEW_BADGES ? (
                  <span className="cnt">
                    <NewBadge />
                  </span>
                ) : n.count && counts[n.count] ? (
                  <span className="cnt">{counts[n.count]}</span>
                ) : null}
              </Link>
            ),
          )}
        </nav>
        <div className="side-fill" />
        <div className="me">
          <div className="me-top">
            <span className="avatar">{name.slice(0, 1).toUpperCase()}</span>
            <div>
              <b>{name}</b>
              <small>
                Mục tiêu {me.profile.goalScore ?? "—"}+ · {score ?? "—"}/990
              </small>
            </div>
            <button
              className="me-chip"
              data-tip={
                proActive ? "Gói Pro|Bấm để xem gói hiện tại" : "Bản miễn phí|Bấm để xem Pro"
              }
              onClick={() => {
                go("/pro");
              }}
            >
              {proActive ? "Pro" : "miễn phí"}
            </button>
          </div>
          <div className="bar">
            <i style={{ width: `${score ? Math.min(100, (score / 990) * 100).toFixed(1) : 0}%` }} />
          </div>
          <div className="skins">
            <span>Giao diện</span>
            <span className="dots">
              {SKIN_DOTS.map(([skin, color]) => (
                <button
                  key={skin}
                  className="skin-dot"
                  style={{ background: color }}
                  aria-label={`Giao diện ${skin}`}
                  aria-pressed={settings.skin === skin}
                  onClick={() => {
                    update({ skin });
                  }}
                />
              ))}
            </span>
          </div>
        </div>
        <div className="others" aria-label="Khác">
          {(
            [
              ["/pro", "👑", "Pro"],
              ["/blog", "📰", "Blog"],
              ["/feedback", "💬", "Góp ý"],
              ["/settings", "⚙", "Cài đặt"],
            ] as const
          ).map(([to, icon, label]) => (
            <button
              key={to}
              aria-label={label}
              aria-current={current(to) ? "page" : undefined}
              onClick={() => {
                go(to);
              }}
            >
              <span>{icon}</span>
              {label}
            </button>
          ))}
          <button
            data-act="collapse"
            className="collapse-btn"
            aria-label={settings.collapsed ? "Mở rộng menu" : "Thu gọn menu"}
            onClick={() => {
              update({ collapsed: !settings.collapsed });
            }}
          >
            <span>{settings.collapsed ? "›" : "‹"}</span>
            {settings.collapsed ? "" : "Thu gọn"}
          </button>
        </div>
      </aside>
      {drawer && (
        <div
          className="side-scrim"
          onClick={() => {
            onDrawer(false);
          }}
        />
      )}
    </>
  );
}
function Topbar({ meta, onDrawer }: { meta: ReturnType<typeof pageMeta>; onDrawer: () => void }) {
  const { me, notify } = useAccount();
  const achievements = useAchievements(me.id);
  return (
    <header className="topbar" id="topbar">
      <button className="menu-btn" aria-label="Mở menu" onClick={onDrawer}>
        ☰
      </button>
      <div className="crumb">
        <SpaceTag space={meta.tag} />
        <span className="sep sp">/</span>
        <span className="here">{meta.label}</span>
      </div>
      <QuickSearch uid={me.id} />
      <StudyIndicator accountId={me.id} />
      <SyncIndicator />
      <button
        className="btn btn-soft tnbtn"
        data-tip="Ghi nhanh|Ghi ở bất kỳ trang nào, lưu về khu Take note"
        onClick={() => {
          window.dispatchEvent(new Event("ca-note-open"));
        }}
      >
        🗒️ <span className="tn-txt">Ghi nhanh</span>
      </button>
      <FocusRuntime key={me.id} uid={me.id} notify={notify} />
      {featureOn("rewards") && (
        <Link
          to="/rewards"
          className="jar"
          data-tip="Bình bong bóng của bạn|+10 🫧 mỗi hiệp tập trung"
          aria-label={`Bình bong bóng: ${achievements.data?.points ?? 0}`}
        >
          🫧 <span>{achievements.data?.points ?? 0}</span>
        </Link>
      )}
      <button
        className="btn btn-dark endbtn"
        data-tip="Kết thúc hôm nay|Xem tổng kết và hẹn lịch ngày mai"
        aria-label="Kết thúc hôm nay"
        onClick={() => {
          window.dispatchEvent(new Event("ca-day-end"));
        }}
      >
        ⏹ <span className="eb-txt">Kết thúc hôm nay</span>
      </button>
    </header>
  );
}
function Tabbar({ path, space }: { path: string; space: Space | null }) {
  const navigate = useNavigate();
  const go = (to: string) => {
    void navigate(to === "/" ? { to: "/" } : { to: "/$", params: { _splat: to.slice(1) } });
  };
  const cur = (key: Space) => (space === key ? "page" : undefined);
  return (
    <nav className="tabbar" id="tabbar" aria-label="Thanh dưới">
      <button
        aria-current={path === "/" ? "page" : undefined}
        onClick={() => {
          go("/");
        }}
      >
        <span>🏠</span>Hôm nay
      </button>
      <button
        aria-current={cur("study")}
        onClick={() => {
          go("/study");
        }}
      >
        <span>📝</span>Làm bài
      </button>
      <button
        aria-current={cur("gram")}
        onClick={() => {
          go("/grammar");
        }}
      >
        <span>📚</span>Ngữ pháp
      </button>
      <button
        className="fab"
        aria-label="Tập trung"
        onClick={() => {
          window.dispatchEvent(new Event("ca-focus-open"));
        }}
      >
        <span>⏱</span>Tập trung
      </button>
      <button
        aria-current={cur("prod")}
        onClick={() => {
          go("/report");
        }}
      >
        <span>📊</span>Năng suất
      </button>
      <button
        aria-current={cur("note")}
        onClick={() => {
          go("/notes");
        }}
      >
        <span>📒</span>Take note
      </button>
    </nav>
  );
}
function Shell() {
  const { me, save, notify } = useAccount();
  const path = useLocation().pathname;
  const meta = pageMeta(path);
  const [drawer, setDrawer] = useState(false);
  // Màn chung (Hôm nay, Blog, Góp ý, Pro, Cài đặt) giữ khu đang mở trên menu trái như bản mẫu.
  const [lastSpace, setLastSpace] = useState<Space>("study");
  const space = meta.space ?? lastSpace;
  useEffect(() => {
    if (meta.space) setLastSpace(meta.space);
  }, [meta.space]);
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setDrawer(false);
  }, [path]);
  const settings = me.profile.settings;
  const update = (patch: Partial<Settings>) => {
    void save(
      "/v1/me/settings",
      "PATCH",
      { expectedRev: me.profile.rev, settings: patch },
      {
        ...me,
        profile: { ...me.profile, rev: me.profile.rev + 1, settings: { ...settings, ...patch } },
      },
    ).catch((e: unknown) => {
      notify(String(e));
    });
  };
  if (path === "/welcome")
    return (
      <main className="gate">
        <Onboarding />
      </main>
    );
  return (
    <div
      className={`app${settings.collapsed ? " collapsed" : ""}${path.startsWith("/attempts/") ? " playing" : ""}`}
      id="app"
    >
      <Side path={path} space={space} drawer={drawer} onDrawer={setDrawer} update={update} />
      <div className="main">
        <Topbar
          meta={meta}
          onDrawer={() => {
            setDrawer(true);
          }}
        />
        <main id="screen" className="screen" key={path}>
          <Page />
        </main>
      </div>
      <Tabbar path={path} space={meta.space} />
      <DictionaryLayer uid={me.id} notify={notify} />
      <NoteRuntime key={me.id} uid={me.id} notify={notify} />
      <UpsellDialog accountId={me.id} />
      <DayEnd account={me} notify={notify} />
    </div>
  );
}
function Page() {
  const path = useLocation().pathname;
  const navigate = useNavigate();
  const { me, notify } = useAccount();
  const today = useToday(me.id);
  const localDate =
    today.data?.date ??
    new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Ho_Chi_Minh" }).format(new Date());
  useEffect(() => {
    if (path === "/drill" || path.startsWith("/drill/"))
      void navigate({
        to: "/$",
        params: { _splat: legacyDrillRoute(Number(path.split("/")[2])).slice(1) },
        replace: true,
      });
  }, [path, navigate]);
  if (hiddenPath(path)) return <NotFound />;
  if (path === "/") return <TodayPage key={me.id} account={me} notify={notify} />;
  if (path === "/report") return <ReportPage key={me.id} account={me} notify={notify} />;
  if (["/grammar", "/vocab", "/errors", "/dictation"].includes(path))
    return <RevisionPage key={`${me.id}:${path}`} account={me} path={path} notify={notify} />;
  if (path === "/notes" || path.startsWith("/notes/") || path.startsWith("/notebook/"))
    return (
      <NotesPage
        key={`${me.id}:${path}`}
        uid={me.id}
        path={path}
        notify={notify}
        isAdmin={me.role === "admin"}
      />
    );
  if (path === "/blog" || path.startsWith("/blog/")) return <Blog uid={me.id} notify={notify} />;
  if (path === "/feedback") return <Feedback uid={me.id} notify={notify} />;
  if (path === "/focus") return <FocusPage uid={me.id} />;
  if (path === "/plan") return <PlannerPage uid={me.id} date={localDate} notify={notify} />;
  if (path === "/habits") return <HabitsPage uid={me.id} date={localDate} notify={notify} />;
  if (path === "/rewards") return <RewardsPage uid={me.id} notify={notify} />;
  if (path === "/game") return <BreakGame uid={me.id} notify={notify} />;
  if (path === "/settings") return <SettingsPage />;
  if (path === "/pro") return <ProPage key={me.id} accountId={me.id} notify={notify} />;
  if (
    ["/study", "/tests", "/mocks", "/scores"].includes(path) ||
    path.startsWith("/attempts/") ||
    path.startsWith("/practice/")
  )
    return <LearningPage key={me.id} account={me} path={path} notify={notify} />;
  return <NotFound />;
}
function NotFound() {
  return (
    <>
      <PageHead
        space="common"
        title="Không tìm thấy trang"
        desc="Đường dẫn này không có trong Cá Toeic."
      />
      <EmptyState title="Quay lại Hôm nay để tiếp tục học">
        <Link to="/" className="btn btn-acc">
          🏠 Về Hôm nay
        </Link>
      </EmptyState>
    </>
  );
}
const GOALS: [number, string][] = [
  [450, "Chuẩn đầu ra cơ bản"],
  [600, "Đi làm / xét lương"],
  [750, "Vị trí tốt, thăng tiến"],
  [850, "Học bổng, công ty nước ngoài"],
  [990, "Gần như tuyệt đối"],
];
const LEVELS: [string, string][] = [
  ["beginner", "Mới bắt đầu"],
  ["basic", "Có nền tảng cơ bản"],
  ["intermediate", "Đang luyện đề"],
  ["advanced", "Muốn nâng điểm cao"],
];
const SLOTS: [string, string, string][] = [
  ["🌅 Sáng", "06:00", "07:00"],
  ["🌤 Trưa", "12:00", "13:00"],
  ["🌙 Tối", "20:00", "21:00"],
  ["🦉 Khuya", "22:00", "23:00"],
];
/** Ba bước làm quen, theo hộp `openOnboard` của bản mẫu. */
function Onboarding() {
  const { me, save, notify } = useAccount();
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState(600);
  const [level, setLevel] = useState("basic");
  const [minutes, setMinutes] = useState(60);
  const [slot, setSlot] = useState<[string, string]>(["20:00", "21:00"]);
  const [name, setName] = useState(me.name ?? "Bạn");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function finish() {
    setBusy(true);
    setError("");
    try {
      const body = ProfileInput.parse({
        expectedRev: me.profile.rev,
        displayName: name,
        goalScore: goal,
        currentLevel: level,
        dailyGoalMinutes: minutes,
        studySlot: { from: slot[0], to: slot[1] },
        examDate: null,
      });
      await save("/v1/me/profile", "PUT", body, {
        ...me,
        needsOnboarding: false,
        profile: {
          ...me.profile,
          ...ProfileInput.omit({ expectedRev: true }).strip().parse(body),
          rev: me.profile.rev + 1,
        },
      });
      notify("Cá đã ghi nhớ mục tiêu của bạn.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Chưa lưu được.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="modal">
      <div className="modal-head">
        <div className="row" style={{ gap: 6 }} aria-label={`Bước ${step + 1}/3`}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: i === step ? 26 : 8,
                height: 8,
                borderRadius: 99,
                background: i === step ? "var(--ink)" : "var(--line-2)",
              }}
            />
          ))}
        </div>
      </div>
      <div className="stack" style={{ gap: 14 }}>
        {step === 0 && (
          <>
            <div style={{ width: 130, margin: "0 auto" }}>
              <Fish />
            </div>
            <h2 style={{ fontSize: 26 }}>Mục tiêu của bạn là bao nhiêu?</h2>
            <p className="muted">
              Chọn điểm TOEIC muốn đạt, Cá thiết kế lộ trình theo mục tiêu này.
            </p>
            <div className="field">
              <label htmlFor="ob-name">Tên của bạn</label>
              <input
                id="ob-name"
                className="input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                maxLength={80}
              />
            </div>
            <div className="grid g2" role="radiogroup" aria-label="Mục tiêu điểm">
              {GOALS.map(([v, sub]) => (
                <button
                  key={v}
                  className="opt"
                  role="radio"
                  aria-checked={goal === v}
                  onClick={() => {
                    setGoal(v);
                  }}
                >
                  <span>
                    <b>{v}+</b>
                    <small>{sub}</small>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2 style={{ fontSize: 26 }}>Bạn đang ở đâu?</h2>
            <p className="muted">Cá dùng trình độ hiện tại để gợi ý part và level bắt đầu.</p>
            <div className="eyebrow">Trình độ hiện tại</div>
            <div
              className="stack"
              role="radiogroup"
              aria-label="Trình độ hiện tại"
              style={{ gap: 8 }}
            >
              {LEVELS.map(([v, label]) => (
                <button
                  key={v}
                  className="opt"
                  role="radio"
                  aria-checked={level === v}
                  onClick={() => {
                    setLevel(v);
                  }}
                >
                  <span>
                    <b>{label}</b>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <SpaceTag space="prod" />
            <h2 style={{ fontSize: 26 }}>Mỗi ngày bạn dành bao lâu?</h2>
            <p className="muted">
              Cá dùng con số này làm vòng mục tiêu mỗi ngày và xếp lịch tuần đầu tiên cho bạn.
            </p>
            <Seg
              label="Phút học mỗi ngày"
              options={[15, 30, 60, 90, 120].map((v) => ({ value: v, label: `${v} phút` }))}
              value={minutes}
              onChange={setMinutes}
            />
            <div className="eyebrow">Thường học lúc nào?</div>
            <Seg
              label="Giờ học"
              options={SLOTS.map(([label, from, to]) => ({ value: `${from}-${to}`, label }))}
              value={`${slot[0]}-${slot[1]}`}
              onChange={(v) => {
                const [from = "20:00", to = "21:00"] = v.split("-");
                setSlot([from, to]);
              }}
            />
          </>
        )}
        {error && <p role="alert">{error}</p>}
        <div className="row">
          {step > 0 && (
            <Button
              variant="soft"
              size="lg"
              onClick={() => {
                setStep(step - 1);
              }}
            >
              Quay lại
            </Button>
          )}
          <Button
            size="lg"
            disabled={busy || !name.trim()}
            onClick={() => {
              if (step < 2) setStep(step + 1);
              else void finish();
            }}
          >
            {step === 2 ? "Bắt đầu cùng Cá" : "Tiếp tục"}
          </Button>
        </div>
      </div>
    </div>
  );
}
/** Cài đặt (QUYET-DINH S2, S3): PageHead tag CHUNG, Đăng xuất cuối trang. */
function SettingsPage() {
  const { me, save, notify } = useAccount();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.profile.displayName ?? me.name ?? "Bạn");
  const [voice, setVoice] = useState(me.profile.settings.voice);
  const [rounds, setRounds] = useState(me.profile.settings.focusRounds);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      const body = SettingsInput.parse({
        expectedRev: me.profile.rev,
        displayName: name,
        settings: { voice, focusRounds: rounds },
      });
      await save("/v1/me/settings", "PATCH", body, {
        ...me,
        profile: {
          ...me.profile,
          displayName: name,
          rev: me.profile.rev + 1,
          settings: { ...me.profile.settings, voice, focusRounds: rounds },
        },
      });
    } catch (e) {
      notify(e instanceof Error ? e.message : "Không lưu được.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead
        space="common"
        title="Cài đặt"
        desc="Tên, giao diện và giọng đọc đi cùng bạn trên các thiết bị."
      />
      <div className="grid g2">
        <Card className="stack">
          <h2>Hồ sơ</h2>
          <div className="field">
            <label htmlFor="settings-name">Tên hiển thị</label>
            <input
              id="settings-name"
              className="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              maxLength={80}
            />
          </div>
          <div className="field">
            <label id="settings-voice">Giọng đọc</label>
            <Seg
              label="Giọng đọc"
              options={[
                { value: "en-US" as const, label: "Anh Mỹ" },
                { value: "en-GB" as const, label: "Anh Anh" },
              ]}
              value={voice}
              onChange={setVoice}
            />
          </div>
          <div className="field">
            <label id="settings-rounds">Mục tiêu hiệp tập trung mỗi ngày</label>
            <input
              aria-labelledby="settings-rounds"
              className="input"
              style={{ width: 120 }}
              type="number"
              inputMode="numeric"
              min={1}
              max={99}
              value={rounds}
              onChange={(e) => {
                setRounds(Math.max(1, Math.min(99, Math.round(Number(e.target.value) || 1))));
              }}
            />
          </div>
          <div>
            <Button
              variant="acc"
              disabled={busy}
              onClick={() => {
                void submit();
              }}
            >
              Lưu cài đặt
            </Button>
          </div>
        </Card>
        <Card className="stack">
          <h2>Tài khoản</h2>
          <p className="muted">
            Đăng nhập bằng Google · {me.email}. Đăng xuất trên máy này không xoá dữ liệu đã đồng bộ.
          </p>
          <div>
            <Button
              variant="line"
              onClick={() => {
                void (async () => {
                  queue.activate(null);
                  try {
                    await revokeLocalPush(me.id).catch(() => {});
                    await logout();
                    sessionStorage.removeItem("ca-account");
                    queryClient.removeQueries({ queryKey: ["me"] });
                    await navigate({ to: "/login" });
                  } catch (e) {
                    queue.activate(me.id);
                    notify(e instanceof Error ? e.message : "Chưa đăng xuất được.");
                  }
                })();
              }}
            >
              Đăng xuất
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
const rootRoute = createRootRoute({ component: Root });
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
});
const newsletterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/newsletter",
  component: NewsletterConfirmation,
});
const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Layout });
const fallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: Layout,
});
const router = createRouter({
  routeTree: rootRoute.addChildren([loginRoute, newsletterRoute, homeRoute, fallbackRoute]),
});
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const root = document.getElementById("root");
if (!root) throw new Error("Missing root");
createRoot(root).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p role="alert">Có lỗi xảy ra. Tải lại trang để tiếp tục.</p>}>
      <QueryClientProvider client={client}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

void cacheOfflineShell();
